import { useCallback, useEffect, useRef, useState } from "react";
import "./NotificationToast.css";

/**
 * Один тост внутри `.notification-center`. Позиционирует его контейнер
 * (flex-стек), сам тост только управляет своим временем жизни:
 * — таймер на паузе, пока тост под курсором или в фокусе;
 * — `revision` перезапускает таймер, когда тот же тост обновили/повторили;
 * — `duration = 0` — тост висит до закрытия.
 */
export type NotificationToastProps = {
  type: "error" | "success" | "warning" | "info";
  title: string;
  message: string;
  /** Через сколько мс скрывать автоматически; 0 — не скрывать. */
  duration: number;
  revision?: number;
  action?: { label: string; onClick: () => void };
  /** Колбэк после анимации скрытия */
  onClose: () => void;
};

const EXIT_ANIMATION_MS = 200;

export function NotificationToast({
  type,
  title,
  message,
  duration,
  revision = 0,
  action,
  onClose,
}: NotificationToastProps) {
  const [open, setOpen] = useState(true);
  const [paused, setPaused] = useState(false);
  const remainingRef = useRef(duration);
  const startedAtRef = useRef(0);

  // Повтор того же уведомления — полный срок заново.
  useEffect(() => {
    remainingRef.current = duration;
    setOpen(true);
  }, [duration, revision]);

  useEffect(() => {
    if (!open || paused || duration <= 0) return;
    startedAtRef.current = Date.now();
    const id = window.setTimeout(() => setOpen(false), remainingRef.current);
    return () => {
      window.clearTimeout(id);
      remainingRef.current = Math.max(
        1000,
        remainingRef.current - (Date.now() - startedAtRef.current)
      );
    };
  }, [open, paused, duration, revision]);

  // Колбэк родителя меняется на каждый рендер (прогресс задач перерисовывает
  // центр часто) — через ref, чтобы не перезапускать таймер скрытия.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (open) return;
    const id = window.setTimeout(() => onCloseRef.current(), EXIT_ANIMATION_MS);
    return () => window.clearTimeout(id);
  }, [open]);

  const pause = useCallback(() => setPaused(true), []);
  const resume = useCallback(() => setPaused(false), []);

  const isError = type === "error";

  return (
    <div
      className={`et-toast et-toast--${type} ${open ? "" : "et-hide"}`}
      role={isError ? "alert" : "status"}
      aria-live={isError ? "assertive" : "polite"}
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
    >
      <div className="et-body">
        <p className="et-title">{title}</p>
        {message && <p className="et-text">{message}</p>}
        {action && (
          <button
            type="button"
            className="et-action"
            onClick={() => {
              action.onClick();
              setOpen(false);
            }}
          >
            {action.label}
          </button>
        )}
      </div>
      <button
        type="button"
        className="et-close"
        aria-label="Закрыть уведомление"
        onClick={() => setOpen(false)}
      >
        ×
      </button>
    </div>
  );
}
